import React, { useEffect, useState } from "react";
import { KeyRound, Copy, Trash2, Loader2, Plus, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { API_BASE } from "@/lib/api";

const ApiKeys = () => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [label, setLabel] = useState("");
  const [newKey, setNewKey] = useState(null); // shown once

  const load = async () => {
    try {
      const { data } = await api.get("/keys");
      setKeys(data.items || []);
    } catch {
      /* non-fatal */
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const createKey = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/keys", { label: label || "My API key" });
      setNewKey(data.key);
      setLabel("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create key.");
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (key_id) => {
    if (!window.confirm("Revoke this API key? Any app or GPT using it will stop working immediately.")) return;
    try {
      await api.delete(`/keys/${key_id}`);
      toast.success("Key revoked.");
      await load();
    } catch {
      toast.error("Couldn't revoke key.");
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success("Copied to clipboard.");
  };

  return (
    <div className="mt-10 pt-8 border-t border-white/10" data-testid="api-keys-section">
      <div className="flex items-center gap-2 text-cyan-300 font-mono text-[10px] uppercase tracking-[0.3em] mb-2">
        <KeyRound size={13} /> Personal API Keys
      </div>
      <p className="text-sm text-slate-400 max-w-2xl">
        Create a key to publish from external tools (e.g. a ChatGPT Custom GPT). A key only posts to
        <span className="text-slate-200"> your own</span> connected accounts, and can be revoked anytime.
        The full key is shown <span className="text-slate-200">once</span> — copy it immediately.
      </p>

      <div className="mt-4 flex flex-wrap gap-2 items-center">
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Key name (e.g. ChatGPT)"
          data-testid="api-key-label"
          className="px-3 py-2 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-sm rounded-sm w-56"
        />
        <button
          onClick={createKey}
          disabled={creating}
          data-testid="api-key-create"
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#00F0FF] text-[#050B14] font-bold rounded-sm text-xs uppercase tracking-widest hover:bg-[#33F3FF] disabled:opacity-50"
        >
          {creating ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Generate key
        </button>
      </div>

      {newKey && (
        <div className="mt-4 p-4 bg-[#0A192F] border border-emerald-400/30 rounded-sm" data-testid="api-key-new">
          <div className="flex items-center gap-2 text-emerald-300 text-xs font-mono uppercase tracking-widest mb-2">
            <ShieldCheck size={13} /> New key — copy it now, you won't see it again
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs text-white bg-[#050B14] px-3 py-2 rounded-sm break-all">{newKey}</code>
            <button onClick={() => copy(newKey)} data-testid="api-key-copy"
              className="inline-flex items-center gap-1 px-3 py-2 border border-cyan-400/40 text-cyan-300 hover:border-cyan-400 rounded-sm text-xs">
              <Copy size={13} /> Copy
            </button>
          </div>
          <p className="text-[11px] text-slate-400 mt-2">
            Use as <code className="text-slate-300">Authorization: Bearer {`{key}`}</code> against{" "}
            <code className="text-slate-300">{API_BASE}/public/publish</code>. OpenAPI schema:{" "}
            <a href={`${API_BASE}/public/openapi.json`} target="_blank" rel="noreferrer" className="text-cyan-300 underline">/public/openapi.json</a>
          </p>
        </div>
      )}

      <div className="mt-5 space-y-2">
        {loading ? (
          <Loader2 className="animate-spin text-slate-500" size={18} />
        ) : keys.length === 0 ? (
          <p className="text-sm text-slate-500">No API keys yet.</p>
        ) : (
          keys.map((k) => (
            <div key={k.key_id} data-testid={`api-key-row-${k.key_id}`}
              className={`flex items-center justify-between p-3 bg-[#0A192F] border rounded-sm ${k.revoked ? "border-white/5 opacity-50" : "border-white/10"}`}>
              <div className="min-w-0">
                <p className="text-sm text-white truncate">
                  {k.label} {k.revoked && <span className="text-red-300 text-[10px] uppercase ml-2">Revoked</span>}
                </p>
                <p className="text-[11px] font-mono text-slate-400">
                  {k.prefix} · created {(k.created_at || "").slice(0, 10)}
                  {k.last_used_at ? ` · last used ${k.last_used_at.slice(0, 10)}` : " · never used"}
                </p>
              </div>
              {!k.revoked && (
                <button onClick={() => revoke(k.key_id)} data-testid={`api-key-revoke-${k.key_id}`}
                  className="inline-flex items-center gap-1 text-[11px] text-red-300 hover:text-red-200 shrink-0">
                  <Trash2 size={13} /> Revoke
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ApiKeys;
